import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import {
  AlertTriangle, Lock, Truck, CheckCircle2, Loader2,
  RotateCcw, EyeOff, History, ChevronRight,
} from 'lucide-react';

import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { Textarea } from '../ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const API = process.env.REACT_APP_BACKEND_URL;

const REASON_OPTIONS = [
  { value: 'rain_day',     label: 'Rain day',              hint: 'Weather forced today off' },
  { value: 'weather',      label: 'Other weather',         hint: 'Wind, snow, freeze, etc.' },
  { value: 'tech_callout', label: 'Tech callout',          hint: 'A technician is unavailable' },
  { value: 'rebuild',      label: 'Rebuild route',         hint: 'Starting Build Day over' },
  { value: 'other',        label: 'Other',                 hint: 'Requires a note' },
];

function formatDateLong(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(`${dateStr}T12:00:00`);
    return d.toLocaleDateString('en-US', {
      weekday: 'long', month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return dateStr; }
}

function formatRelativeTime(iso) {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    const diffMin = Math.round((Date.now() - t) / 60000);
    if (diffMin < 1)  return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.round(diffHr / 24);
    return `${diffDay}d ago`;
  } catch { return ''; }
}

const SCOPE_COPY = {
  technician: {
    title: 'Reset technician route',
    description: (techName) =>
      `Return ${techName || 'this technician'}'s assigned jobs to the Unassigned column. ` +
      `Locked appointments and in-flight stops stay where they are.`,
  },
  day: {
    title: 'Reset day',
    description: () =>
      'Return all route-mode jobs on this day to the Unassigned column across all technicians. ' +
      'Locked appointments, in-flight stops, and completed work stay where they are.',
  },
};

/**
 * Shared Reset Sheet — used by both "Reset Day" and per-tech "Reset route".
 *
 * Contract:
 *   • Recovery destination is ALWAYS Unassigned.
 *   • No status, scheduledStart, recurrence, jobMode, or lifecycle data is modified.
 *   • Appointments and in-flight jobs (en_route/on_site) are preserved.
 *   • Two-step: server preview → confirm → server apply.
 */
export default function ResetSheet({
  open,
  onOpenChange,
  companyId,
  dispatchDate,    // JS Date
  scope,           // "technician" | "day"
  techUid,         // required iff scope === "technician"
  techName,        // for header copy
}) {
  const [tab, setTab] = useState('preview');
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [preview, setPreview] = useState(null);
  const [reason, setReason] = useState('rain_day');
  const [reasonNote, setReasonNote] = useState('');

  const [auditLoading, setAuditLoading] = useState(false);
  const [auditEvents, setAuditEvents] = useState([]);

  const dateStr = useMemo(() => {
    if (!dispatchDate) return '';
    const d = new Date(dispatchDate);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  }, [dispatchDate]);

  const fetchPreview = useCallback(async () => {
    if (!companyId || !dateStr || !scope) return;
    if (scope === 'technician' && !techUid) return;
    setLoading(true);
    setPreview(null);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await axios.post(
        `${API}/api/dispatch/reset/preview`,
        { companyId, scope, date: dateStr, techUid: scope === 'technician' ? techUid : null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setPreview(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Could not load preview';
      toast.error(detail);
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  }, [companyId, dateStr, scope, techUid, onOpenChange]);

  const fetchAudit = useCallback(async () => {
    if (!companyId || !dateStr) return;
    setAuditLoading(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await axios.get(
        `${API}/api/dispatch/reset/audit`,
        { params: { companyId, date: dateStr, limit: 10 },
          headers: { Authorization: `Bearer ${token}` } },
      );
      setAuditEvents(res.data?.events || []);
    } catch (err) {
      // Audit is non-critical — fail quietly with a small note in the tab.
      setAuditEvents([]);
    } finally {
      setAuditLoading(false);
    }
  }, [companyId, dateStr]);

  // Reset state and refetch each time the sheet opens.
  useEffect(() => {
    if (open) {
      setTab('preview');
      setReason('rain_day');
      setReasonNote('');
      fetchPreview();
      fetchAudit();
    }
  }, [open, fetchPreview, fetchAudit]);

  const isOtherReason = reason === 'other';
  const reasonNoteTrim = (reasonNote || '').trim();
  const reasonInvalid = isOtherReason && reasonNoteTrim.length === 0;
  const nothingToDo = preview && preview.summary.plannedUnassignCount === 0;
  const canApply = !!preview && !nothingToDo && !applying && !loading && !reasonInvalid;

  const handleApply = useCallback(async () => {
    if (!preview || !canApply) return;
    setApplying(true);
    try {
      const token = await getAuth().currentUser?.getIdToken();
      const res = await axios.post(
        `${API}/api/dispatch/reset/apply`,
        {
          companyId,
          scope,
          date: dateStr,
          techUid: scope === 'technician' ? techUid : null,
          reason,
          reasonNote: reasonNoteTrim,
          inputDigest: preview.inputDigest,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const count = res.data?.appliedCount ?? 0;
      toast.success(
        scope === 'day'
          ? `Reset ${count} job${count !== 1 ? 's' : ''} for ${formatDateLong(dateStr)}`
          : `Reset ${techName || 'route'} — ${count} job${count !== 1 ? 's' : ''} returned to Unassigned`,
      );
      onOpenChange(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || 'Reset failed';
      if (err?.response?.status === 409) {
        toast.error('Plan is stale — refresh and try again.');
        // Refresh the preview so the operator sees the latest state.
        fetchPreview();
      } else {
        toast.error(detail);
      }
    } finally {
      setApplying(false);
    }
  }, [preview, canApply, companyId, scope, dateStr, techUid, reason, reasonNoteTrim,
      techName, onOpenChange, fetchPreview]);

  const copy = SCOPE_COPY[scope] || SCOPE_COPY.day;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl overflow-y-auto"
        data-testid="reset-sheet"
      >
        <SheetHeader>
          <div className="flex items-center gap-2">
            <RotateCcw className="h-5 w-5 text-amber-600" />
            <SheetTitle>{copy.title}</SheetTitle>
          </div>
          <SheetDescription className="text-sm">
            {copy.description(techName)}
            <br />
            <span className="text-gray-500">
              {formatDateLong(dateStr)} — jobs stay on this date; only the assignment is cleared.
            </span>
          </SheetDescription>
        </SheetHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-4">
          <TabsList className="w-full">
            <TabsTrigger value="preview" className="flex-1" data-testid="reset-tab-preview">
              Preview
            </TabsTrigger>
            <TabsTrigger value="activity" className="flex-1" data-testid="reset-tab-activity">
              <History className="h-3.5 w-3.5 mr-1.5" /> Activity
            </TabsTrigger>
          </TabsList>

          {/* ─── Preview Tab ─────────────────────────────────────────────── */}
          <TabsContent value="preview" className="mt-4 space-y-4">
            {loading && (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
              </div>
            )}

            {!loading && preview && (
              <>
                <SummaryStrip preview={preview} />
                <BucketList
                  title="Will return to Unassigned"
                  icon={<RotateCcw className="h-4 w-4 text-amber-600" />}
                  tone="amber"
                  items={preview.willUnassign}
                  emptyText={
                    scope === 'technician'
                      ? 'No assigned route jobs to reset for this technician.'
                      : 'No assigned route jobs to reset on this day.'
                  }
                  testId="will-unassign"
                />
                <BucketList
                  title="Locked appointments — staying"
                  icon={<Lock className="h-4 w-4 text-gray-500" />}
                  tone="gray"
                  items={preview.willKeep.appointments}
                  emptyText="No locked appointments."
                  testId="keep-appointments"
                  hideWhenEmpty
                />
                <BucketList
                  title="In-flight — staying"
                  icon={<Truck className="h-4 w-4 text-indigo-600" />}
                  tone="indigo"
                  items={preview.willKeep.inFlight}
                  emptyText="No in-flight jobs."
                  testId="keep-inflight"
                  hideWhenEmpty
                />
                <BucketList
                  title="Completed / cancelled — staying"
                  icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
                  tone="emerald"
                  items={preview.willKeep.terminal}
                  emptyText="No completed jobs."
                  testId="keep-terminal"
                  hideWhenEmpty
                />
                {preview.willKeep.hiddenClosed > 0 && (
                  <div
                    className="rounded-md border border-gray-200 bg-gray-50 p-3 text-xs text-gray-600 flex items-center gap-2"
                    data-testid="keep-hidden-closed"
                  >
                    <EyeOff className="h-3.5 w-3.5 text-gray-500" />
                    {preview.willKeep.hiddenClosed} job
                    {preview.willKeep.hiddenClosed !== 1 ? 's' : ''} on closed accounts
                    {' — '}hidden from this view, not modified.
                  </div>
                )}

                {/* Reason picker — required */}
                <div className="border-t pt-4 space-y-3">
                  <Label className="text-sm font-semibold text-gray-900">
                    Why are you resetting? <span className="text-red-500">*</span>
                  </Label>
                  <RadioGroup
                    value={reason}
                    onValueChange={setReason}
                    className="space-y-1.5"
                    data-testid="reset-reason-group"
                  >
                    {REASON_OPTIONS.map((opt) => (
                      <label
                        key={opt.value}
                        htmlFor={`reset-reason-${opt.value}`}
                        className="flex items-start gap-2.5 cursor-pointer p-2 rounded-md hover:bg-gray-50"
                      >
                        <RadioGroupItem
                          id={`reset-reason-${opt.value}`}
                          value={opt.value}
                          data-testid={`reset-reason-${opt.value}`}
                        />
                        <div className="text-sm leading-tight">
                          <div className="font-medium text-gray-900">{opt.label}</div>
                          <div className="text-xs text-gray-500">{opt.hint}</div>
                        </div>
                      </label>
                    ))}
                  </RadioGroup>

                  <div>
                    <Label htmlFor="reset-reason-note" className="text-xs text-gray-600">
                      Note {isOtherReason
                        ? <span className="text-red-500">(required for Other)</span>
                        : <span className="text-gray-400">(optional)</span>}
                    </Label>
                    <Textarea
                      id="reset-reason-note"
                      value={reasonNote}
                      onChange={(e) => setReasonNote(e.target.value.slice(0, 200))}
                      placeholder="Anything dispatch should remember about this reset"
                      className="mt-1 text-sm"
                      rows={2}
                      data-testid="reset-reason-note"
                    />
                    <div className="text-[10px] text-gray-400 text-right mt-0.5">
                      {reasonNote.length}/200
                    </div>
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* ─── Activity Tab ────────────────────────────────────────────── */}
          <TabsContent value="activity" className="mt-4">
            <ActivityList
              loading={auditLoading}
              events={auditEvents}
              dateLabel={formatDateLong(dateStr)}
            />
          </TabsContent>
        </Tabs>

        {/* ─── Footer ────────────────────────────────────────────────────── */}
        <div className="border-t mt-6 pt-4 flex items-center justify-between gap-2 sticky bottom-0 bg-white">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={applying}
            data-testid="reset-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={!canApply}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="reset-apply-btn"
          >
            {applying ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Resetting…</>
            ) : nothingToDo ? (
              'Nothing to reset'
            ) : (
              `Return ${preview?.summary.plannedUnassignCount ?? 0} job${
                preview && preview.summary.plannedUnassignCount === 1 ? '' : 's'
              } to Unassigned`
            )}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

// ─── Subcomponents ───────────────────────────────────────────────────────────

function SummaryStrip({ preview }) {
  const s = preview.summary;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
      <Stat label="Will unassign" value={s.plannedUnassignCount} tone="amber" testId="summary-unassign" />
      <Stat label="Locked"        value={s.lockedCount}    tone="gray" />
      <Stat label="In-flight"     value={s.inFlightCount}  tone="indigo" />
      <Stat label="Completed"     value={s.terminalCount}  tone="emerald" />
    </div>
  );
}

function Stat({ label, value, tone, testId }) {
  const toneClass = {
    amber:   'border-amber-200 bg-amber-50 text-amber-800',
    gray:    'border-gray-200 bg-gray-50 text-gray-700',
    indigo:  'border-indigo-200 bg-indigo-50 text-indigo-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  }[tone] || 'border-gray-200 bg-gray-50 text-gray-700';
  return (
    <div className={`rounded-md border px-2 py-1.5 ${toneClass}`} data-testid={testId}>
      <div className="text-lg font-bold leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-wide mt-0.5">{label}</div>
    </div>
  );
}

function BucketList({ title, icon, tone, items, emptyText, testId, hideWhenEmpty }) {
  if (hideWhenEmpty && (!items || items.length === 0)) return null;
  const headerToneClass = {
    amber:   'text-amber-900',
    gray:    'text-gray-700',
    indigo:  'text-indigo-800',
    emerald: 'text-emerald-800',
  }[tone] || 'text-gray-700';
  return (
    <div className="rounded-md border border-gray-200 overflow-hidden" data-testid={testId}>
      <div className={`flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 ${headerToneClass}`}>
        {icon}
        <span className="text-xs font-semibold">{title}</span>
        <span className="ml-auto text-[10px] text-gray-500">{items?.length ?? 0}</span>
      </div>
      {(!items || items.length === 0) ? (
        <div className="px-3 py-3 text-xs text-gray-500 italic">{emptyText}</div>
      ) : (
        <ul className="divide-y divide-gray-100 max-h-40 overflow-y-auto">
          {items.map((t) => (
            <li key={t.taskId} className="px-3 py-1.5 text-xs flex items-center gap-2">
              <span className="font-medium text-gray-800 truncate flex-1">{t.clientName}</span>
              {t.techName && (
                <span className="text-[10px] text-gray-500 shrink-0">→ {t.techName}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ActivityList({ loading, events, dateLabel }) {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-8 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
      </div>
    );
  }
  if (!events || events.length === 0) {
    return (
      <div className="text-center text-sm text-gray-500 py-8" data-testid="activity-empty">
        No reset activity on {dateLabel}.
      </div>
    );
  }
  return (
    <ul className="space-y-2" data-testid="activity-list">
      {events.map((e) => (
        <li
          key={e.auditId}
          className="rounded-md border border-gray-200 p-3 text-xs"
          data-testid={`activity-row-${e.auditId}`}
        >
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600" />
            <span className="font-semibold text-gray-900">
              {e.scope === 'day' ? 'Reset day' : 'Reset technician route'}
            </span>
            <ChevronRight className="h-3 w-3 text-gray-400" />
            <span className="text-gray-700">
              {(e.reason || '').replace('_', ' ')}
            </span>
            <span className="ml-auto text-[10px] text-gray-500">
              {formatRelativeTime(e.createdAt)}
            </span>
          </div>
          {e.reasonNote && (
            <div className="mt-1 text-gray-600 italic">"{e.reasonNote}"</div>
          )}
          <div className="mt-1 text-gray-500">
            {e.affectedCount} job{e.affectedCount !== 1 ? 's' : ''} returned to Unassigned
            {' · by '}
            {e.actorName || e.actorEmail || 'unknown'}
            {e.actorRole && (
              <span className="text-gray-400"> ({e.actorRole})</span>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
